import { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import classNames from 'classnames';
import { createRoot } from 'react-dom/client';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from '@ag-grid-community/react';
import { ReactComponent as ColumnDragIcon } from 'images/icons/gh_dragg.svg';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { useFeatureFlagQuery } from 'remote-state/featureFlagsHooks';
import { selectRB, toggleSwitchHeadersLanguage, isHeadersLanguageChange } from 'store/userSlice';
import { setSrDetailsForSummary } from 'store/ticketSummarySlice';
import { PERFORMANCE_MEASURE } from 'constants/performanceMeasure';
import { APP_CONSTANTS } from 'constants/index';
import usePerformanceMeasure from 'common/utils/hooks/usePerformanceMeasure';
import PropTypes from 'prop-types';
import { FEATURE_FLAGS_KEYS } from 'constants/featureFlags';
import isEqual from 'lodash-es/isEqual';
import { GRID, COLUMN_FILTER_TYPES } from 'features/queue/grid/constants';
import useTexts from 'features/queue/grid/useTexts';
import CustomColumnHeader from 'features/queue/grid/customColumnHeader';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import ActionBtnRenderer from 'features/queue/grid/actionBtnRenderer';
import EndResultsMessage from 'features/queue/grid/endResultsMessage';
import LoadingOverlayMessage from 'features/queue/grid/loadingOverlayMessage';
import DefaultCellRender from 'features/queue/grid/defaultCellRender';
import useCacheColumnsWidth from 'features/queue/grid/hooks/useCacheColumnsWidth';
import useQueueData from 'features/queue/newGrid/hooks/useQueueData';
import useServerRowModelDataSource from 'features/queue/newGrid/hooks/useServerRowModelDataSource';
import useDefaultPageSize from 'features/queue/newGrid/hooks/useDefaultPageSize';
import { convertFiltersToGridFilterModel } from 'features/queue/grid/helpers';
import { selectFilterModel, selectFullSortModel, selectSearchText } from 'features/queue/slice';
import { processFirstDataRendered, resizeLastPinnedColumn } from 'features/queue/grid/GridComponent';
import { selectDirection, selectDirectionChanged, setDirectionChanged } from 'store/globalSlice';
import { useRbTexts } from 'remote-state/applicationHooks';
import { getCustomHeaderName } from 'features/queue/grid/defaultCellRender/SelectRender/utils';
import useGridApi from 'common/providers/AgGridProvider/hooks/useGridApi';
import useStoreGridState from 'features/queue/newGrid/hooks/useStoreGridState';
import { CACHE_BLOCK_SIZE, PAGE_SIZE_SELECTOR } from 'features/queue/newGrid/consts';
import { StyledNewGridStyle } from 'features/queue/grid/style';

// Using lodash isEqual for deeper comparison
const arePropsEqual = (prevProps, nextProps) => isEqual(prevProps, nextProps);

const GridComponent = memo(
  ({
    scrollToId,
    gridRef,
    context,
    columnDefs,
    handleClosePopup,
    onRowSelected,
    onCellClicked,
    onRowClicked,
    onColumnMoved,
    onFilterOpened,
    tabToNextCell,
    onFirstDataRendered,
    onBodyScroll,
  }) => {
    const { removeQueueData } = useQueueData();
    const dispatch = useDispatch();
    const isHeadersChange = useSelector(isHeadersLanguageChange);
    const direction = useSelector(selectDirection);
    const directionChanged = useSelector(selectDirectionChanged);
    const rbSettings = useSelector(selectRB);
    const filterModel = useSelector(selectFilterModel);
    const sortModel = useSelector(selectFullSortModel);
    const isGridReady = useRef(false);
    const filterModelRef = useRef([]);
    const queueSearchTextRef = useRef();
    const queueSearchText = useSelector(selectSearchText);
    const [, setGridApi] = useGridApi();
    const initServerRowModelDataSource = useServerRowModelDataSource();
    const { saveGridState, restoreGridState } = useStoreGridState();
    const [isGridRendered, setIsGridRendered] = useState(false);
    const defaultPageSize = useDefaultPageSize();

    useEffect(() => {
      queueSearchTextRef.current = queueSearchText;
    }, [queueSearchText]);

    const { data: isRTLEnabled } = useFeatureFlagQuery({
      flagKey: FEATURE_FLAGS_KEYS.RTL_ENABLED,
      defaultValue: false,
    });
    const { of, pageSize } = useTexts();
    const { performanceMeasureEventReport, getEventName } = usePerformanceMeasure();
    const { handleColumnResized } = useCacheColumnsWidth();
    const { data: isSROpenedFromQueue } = useFeatureFlagQuery({
      flagKey: FEATURE_FLAGS_KEYS.OPEN_SR_FROM_QUEUE,
      defaultValue: false,
    });
    const rbTexts = useRbTexts();

    // Memoize more computed values
    const isRTLMemo = useMemo(() => direction === 'rtl' && isRTLEnabled, [direction, isRTLEnabled]);

    const { localeText, languageChange } = useMemo(
      () => ({
        localeText: {
          to: '-',
          of,
          more: '',
          pageSizeSelectorLabel: pageSize,
        },
        languageChange: `grid-${JSON.stringify(rbSettings?.resourceBundleMap)}`,
      }),
      [of, pageSize, rbSettings?.resourceBundleMap],
    );

    const defaultColDef = useMemo(
      () => ({
        filter: 'agTextColumnFilter',
        resizable: true,
        lockPinned: true,
        suppressSizeToFit: 'false',
        filterParams: {
          suppressAndOrCondition: true,
          filterOptions: [COLUMN_FILTER_TYPES.equals, COLUMN_FILTER_TYPES.contains],
          handleClosePopup,
        },
        cellRenderer: DefaultCellRender,
        cellStyle: isSROpenedFromQueue ? { cursor: 'pointer' } : {},
      }),
      [handleClosePopup, isSROpenedFromQueue],
    );

    const setSortModel = useCallback(
      (sortModel) => {
        if (isGridReady.current) {
          gridRef?.current?.api?.applyColumnState({
            state: sortModel,
            defaultState: { sort: null },
          });
        }
      },
      [gridRef],
    );

    const setFilterModel = useCallback(
      (filterModel) => {
        if (isGridReady.current) {
          const convertedFilters = convertFiltersToGridFilterModel(filterModel);
          filterModelRef.current = convertedFilters;
          gridRef?.current?.api?.setFilterModel(convertedFilters);
        }
      },
      [gridRef],
    );

    useEffect(() => {
      setFilterModel(filterModel);
    }, [filterModel, setFilterModel]);

    useEffect(() => {
      setSortModel(sortModel);
    }, [sortModel, setSortModel]);

    const onGridReady = useCallback(
      (params) => {
        if (!params?.api) return;

        setGridApi(params.api);

        removeQueueData();

        isGridReady.current = true;

        if (filterModel?.length) {
          setFilterModel(filterModel);
        }

        if (sortModel?.length) {
          setSortModel(sortModel);
        }

        resizeLastPinnedColumn(params);

        initServerRowModelDataSource({
          filterModelRef,
          queueSearchTextRef,
        });
      },
      [filterModel, initServerRowModelDataSource, removeQueueData, setFilterModel, setGridApi, setSortModel, sortModel],
    );

    const getColumnDraggIcon = useCallback(() => {
      const domNode = document.createElement('div');
      const root = createRoot(domNode);
      root.render(<ColumnDragIcon />);
      return domNode;
    }, []);

    const onCellMouseOver = useCallback(
      (e) => {
        if (Array.isArray(e?.data)) {
          const srId = e?.data?.find((col) => col.field === 'id')?.value;
          const title = e?.data?.find((col) => col.field === 'title')?.value;
          dispatch(setSrDetailsForSummary({ srId, title }));
        }
      },
      [dispatch],
    );

    const getRowId = useCallback(({ data }) => {
      if (data.length > 0) {
        return data.find((sr) => sr?.field === 'id')?.value;
      }
    }, []);

    const onCellEditingStarted = useCallback(
      (e) => {
        onCellClicked(e?.node?.id);
      },
      [onCellClicked],
    );

    const onCellEditingStopped = useCallback(() => {
      onCellClicked(null);
    }, [onCellClicked]);

    const modules = useMemo(() => [ServerSideRowModelModule], []);

    const rowClassRules = useMemo(
      () => ({
        'shadow-inset-center': (params) => params.node.id === parseInt(scrollToId, 10),
      }),
      [scrollToId],
    );

    const components = useMemo(
      () => ({
        agColumnHeader: CustomColumnHeader,
        ActionBtnRenderer,
      }),
      [],
    );

    const normalizedColumnDefs = useMemo(() => {
      const columnDefsArray = !Array.isArray(columnDefs) ? [] : columnDefs;
      const translatedColumnDefsArray = columnDefsArray.map((item) => {
        const headerName = getCustomHeaderName(rbTexts, item?.field) ?? item?.headerName;
        return { ...item, headerName };
      });
      return translatedColumnDefsArray;
    }, [columnDefs, rbTexts]);

    const icons = useMemo(
      () => ({
        columnMoveMove: getColumnDraggIcon,
        columnDrag: getColumnDraggIcon,
        columnMovePin: getColumnDraggIcon,
        columnMoveRight: getColumnDraggIcon,
        columnMoveLeft: getColumnDraggIcon,
      }),
      [getColumnDraggIcon],
    );

    const getRowClass = useCallback((params) => {
      if (params.data === undefined) {
        return 'ag-empty-row';
      }
    }, []);

    useEffect(() => {
      if (columnDefs?.length && gridRef?.current) {
        resizeLastPinnedColumn(gridRef?.current);
      }
    }, [columnDefs, gridRef]);

    useEffect(() => {
      if (gridRef?.current?.api && isHeadersChange) {
        gridRef.current.api.setColumnDefs(columnDefs);
        gridRef.current.api.refreshHeader();
        dispatch(toggleSwitchHeadersLanguage(false));
      }
    }, [columnDefs, dispatch, gridRef, isHeadersChange]);

    const onGridPreDestroyed = useCallback(() => {
      gridRef?.current?.api?.cleanDownReferencesToAvoidMemoryLeakInCaseApplicationIsKeepingReferenceToDestroyedGrid();
    }, [gridRef]);

    const handlePerformanceReport = useCallback(() => {
      performance.mark(PERFORMANCE_MEASURE.QUEUE.DATA_POPULATION);
      const { eventName, startEventName, data = {} } = getEventName();
      if (eventName && startEventName) {
        const startTime = performance.getEntriesByName(startEventName)[0]?.startTime;
        const endTime = performance.getEntriesByName(PERFORMANCE_MEASURE.QUEUE.DATA_POPULATION)[0]?.startTime;
        const duration = endTime - startTime + GRID.RENDER_DATA_TIME;
        performanceMeasureEventReport(eventName, {
          tenantId: localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY),
          duration,
          ...data,
        });
      } else {
        performance.clearMarks(PERFORMANCE_MEASURE.QUEUE.DATA_POPULATION);
      }
    }, [getEventName, performanceMeasureEventReport]);

    const onColumnResized = useCallback(
      (params) => {
        handleColumnResized(params);
      },
      [handleColumnResized],
    );

    const onFilterChanged = useCallback(() => {
      handlePerformanceReport();
    }, [handlePerformanceReport]);

    const onSortChanged = useCallback(() => {
      handlePerformanceReport();
    }, [handlePerformanceReport]);

    const handleFirstDataRendered = useCallback(
      (params) => {
        restoreGridState();
        handlePerformanceReport();
        processFirstDataRendered(params, onFirstDataRendered);
      },
      [handlePerformanceReport, onFirstDataRendered, restoreGridState],
    );

    const onStoreUpdated = useCallback(() => {
      setIsGridRendered(true);
    }, []);

    // Combine all handlers
    const eventHandlers = useMemo(
      () => ({
        onGridPreDestroyed,
        onPaginationChanged: saveGridState,
        onColumnResized,
        onFilterChanged,
        onSortChanged,
        onCellMouseOver,
        onCellEditingStarted,
        onCellEditingStopped,
        onGridReady,
        onFirstDataRendered: handleFirstDataRendered,
        onRowSelected,
        onCellClicked,
        onRowClicked,
        onColumnMoved,
        onFilterOpened,
        tabToNextCell,
        onBodyScroll,
      }),
      [
        onGridPreDestroyed,
        saveGridState,
        onColumnResized,
        onFilterChanged,
        onSortChanged,
        onCellMouseOver,
        onCellEditingStarted,
        onCellEditingStopped,
        onGridReady,
        handleFirstDataRendered,
        onRowSelected,
        onCellClicked,
        onRowClicked,
        onColumnMoved,
        onFilterOpened,
        tabToNextCell,
        onBodyScroll,
      ],
    );

    // Memoize static configuration
    const staticConfig = useMemo(
      () => ({
        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        rowModelType: 'serverSide',
        suppressAnimationFrame: true,
        suppressDragLeaveHidesColumns: true,
        suppressPropertyNamesCheck: true,
        suppressColumnVirtualisation: true,
        reactiveCustomComponents: true,
        headerHeight: 44,
        rowHeight: 44,
        pagination: true,
        paginationPageSizeSelector: PAGE_SIZE_SELECTOR,
        cacheBlockSize: CACHE_BLOCK_SIZE,
        suppressPaginationPanel: false,
        suppressRowVirtualisation: true,
        animateRows: false,
        debug: false,
      }),
      [],
    );

    // Add row cleanup handler
    const handleRowRemoved = useCallback((event) => {
      if (event.node?.rowCtrl?.centerGui) {
        event.node.rowCtrl.centerGui.destroy();
        event.node.rowCtrl.centerGui = null;
      }
    }, []);

    // Memoize grid props
    const gridProps = useMemo(
      () => ({
        localeText,
        enableRtl: Boolean(isRTLMemo),
        modules,
        ref: gridRef,
        columnDefs: normalizedColumnDefs,
        defaultColDef,
        context,
        rowClassRules,
        components,
        icons,
        noRowsOverlayComponent: EndResultsMessage,
        loadingOverlayComponent: LoadingOverlayMessage,
        tabToNextCell,
        navigateToNextCell: tabToNextCell,
        getRowId,
        getRowClass,
        onRowRemoved: handleRowRemoved,
        paginationPageSize: defaultPageSize,
        onStoreUpdated,
        ...eventHandlers,
        ...staticConfig,
      }),
      [
        localeText,
        isRTLMemo,
        modules,
        gridRef,
        normalizedColumnDefs,
        defaultColDef,
        context,
        rowClassRules,
        components,
        icons,
        tabToNextCell,
        getRowId,
        getRowClass,
        handleRowRemoved,
        onStoreUpdated,
        eventHandlers,
        staticConfig,
        defaultPageSize,
      ],
    );

    if (directionChanged) {
      setTimeout(() => dispatch(setDirectionChanged(false)), 3000);
      return null;
    }
    return (
      <StyledNewGridStyle
        className={classNames('ag-theme-alpine', { queueLoaded: isGridRendered })}
        data-testid="queue-grid"
        isVisible={isGridRendered}
      >
        <AgGridReact key={languageChange} {...gridProps} />
      </StyledNewGridStyle>
    );
  },
  arePropsEqual,
);

GridComponent.propTypes = {
  scrollToId: PropTypes.string,
  gridRef: PropTypes.object.isRequired,
  context: PropTypes.object,
  columnDefs: PropTypes.array.isRequired,
  handleClosePopup: PropTypes.func.isRequired,
  onRowSelected: PropTypes.func,
  onCellClicked: PropTypes.func,
  onRowClicked: PropTypes.func,
  handleColumnMoved: PropTypes.func,
  onFilterOpened: PropTypes.func,
  tabToNextCell: PropTypes.func,
  onFirstDataRendered: PropTypes.func,
  onBodyScroll: PropTypes.func,
};

export default GridComponent;
